import { GfdPlaybook } from './types';

const yn = [
  { value: 'Yes', confidence: 'confirmed' as const },
  { value: 'No', confidence: 'confirmed' as const },
];

export const SMBC_GFD_PLAYBOOK: GfdPlaybook = {
  id: 'smbc-gfd-v1',
  name: 'SMBC Global Front Door — Initiative Requestor packet',
  client: 'SMBC',
  version: '1.2',
  persona: 'Initiative Requestor / Submitter',
  interfaceName: 'ServiceNow Employee Center',
  navigation: {
    prodUrl: 'https://smbc.service-now.com/esc',
    uatUrl: 'https://smbcuat.service-now.com/esc',
    steps: [
      'Open a browser (Chrome is safest) and go to the Employee Center — Production: https://smbc.service-now.com/esc (UAT if they asked you to use the test instance: https://smbcuat.service-now.com/esc).',
      'Sign in with your SMBC credentials if prompted.',
      'In the Employee Center search bar at the top, type: Global Initiatives',
      'From the results, click Global Initiatives Intake Request (catalog item). If search is noisy, use Catalog → Business Architecture → Global Initiatives Intake Request.',
      'Click the Request button to open the intake form.',
      'Do not click Save as Draft. Complete the form in one sitting using this packet, then click Submit.',
    ],
  },
  warnings: [
    {
      id: 'save-as-draft',
      severity: 'critical',
      title: 'Do not use Save as Draft',
      text: 'The Save as Draft button is visible but was not functional at go-live. Clicking it will not save the initiative. Complete the form in one sitting from this packet.',
    },
    {
      id: 'thin-problem',
      severity: 'critical',
      title: 'A one-line problem statement will be sent back',
      text: 'Working Group members bounce thin problem statements (e.g. “ServiceNow modernization project”) to Draft. Use the full paste block in this packet — current-state gap, why it matters, and what breaks if nothing changes.',
    },
    {
      id: 'gov-pptx',
      severity: 'important',
      title: 'These fields become the governance PowerPoint',
      text: 'When the idea reaches Final Review, ServiceNow auto-generates a PowerPoint from the Stage 1 fields. There is no later chance to “clean up the story.” Accuracy here is the story the Governance Forum reads.',
    },
    {
      id: 'yellow-lists',
      severity: 'important',
      title: 'Some dropdowns are reconstructed',
      text: 'Where this packet marks Confirm in ServiceNow, the exact wording of the live list was never fully captured. Pick the closest match on screen and tell the consultant what the live options were — that upgrades the playbook.',
    },
    {
      id: 'rtb-checkpoint',
      severity: 'important',
      title: 'Run-the-Bank still goes through this form',
      text: 'RtB / BAU / KTLO / patching / license refresh still complete Stage 1. Working Group currently uses GFD as a governance checkpoint that authorizes BAU execution — not a formal GFD funding vote. Do not skip the form because it is “just maintenance,” and do not promise the champion a Governance Forum vote for RtB.',
    },
    {
      id: 'global-vs-ad',
      severity: 'important',
      title: 'Global Front Door vs Americas Division Front Door',
      text: 'Treat the ask as Global if it hits more than one region, supports a global strategic program, or exceeds the material-spend threshold. Below-threshold regional work may be AD Front Door. The numeric threshold lived in an appendix and was still TBD in the source pack — if unsure, complete this packet as Global and let Working Group re-route.',
    },
  ],
  attachments: [
    {
      id: 'business-case',
      name: 'Business case (Word or PPT)',
      required: false,
      note: 'Attach after Submit via the paperclip on My Request if it will not fit in the form. Not required to submit, but strongly recommended.',
    },
    {
      id: 'cost-support',
      name: 'Cost / headcount support (Excel)',
      required: false,
      note: 'Anything that backs Current Fiscal Year Cost, cost avoidance, and net-new FTEs.',
    },
    {
      id: 'c4l1',
      name: 'C4L1 / Ardoq C4.1 conceptual diagram',
      required: false,
      note: 'Train-the-Trainer calls this an Ardoq C4.1 diagram; training said C4L1. The requestor does not produce it. The assigned Solution Architect attaches it during EA Screening. Do not block submission waiting for it.',
    },
  ],
  intakeGuidance: {
    headline: 'GFD does not require file uploads to submit. It does require enough substance that Working Group will not send the idea back to Draft — and that this workspace is not inventing people, costs, or issue IDs.',
    caveat: 'Notes can stand in for a missing file if they contain the same facts. Extra files help only when they add facts. A logo-only deck plus empty notes still produces a thin packet.',
    minimum: [
      {
        id: 'problem-outcome',
        title: 'Problem and intended outcome',
        detail: 'Current-state gap, why it matters, and what breaks if nothing changes — plus the outcome and how you will measure it. A one-line “ServiceNow modernization project” is what Working Group bounces.',
        unlocks: 'Problem statement, intended outcome, initiative name (these become the auto-generated governance PowerPoint at Final Review).',
      },
      {
        id: 'named-people',
        title: 'Three named people who exist in ServiceNow',
        detail: 'Initiative owner, CxO / senior business executive sponsor, and COO execution lead — legal/directory names, not nicknames.',
        unlocks: 'The three person lookups. If they are not in the SMBC user table, the champion cannot submit.',
      },
      {
        id: 'classification',
        title: 'How to classify the ask',
        detail: 'Change-the-Bank vs Run-the-Bank (RtB still submits); impacted regions (multi-region is a global trigger); remaining-lifecycle / entire-lifecycle cost band — match the live label; delivery timeline; Discovery vs Execution; requesting and beneficiary portfolios; whether this sits under an existing program or project.',
        unlocks: 'Scope & Classification and Portfolio Alignment — the two controls Working Group checks at Feasibility Fit.',
      },
      {
        id: 'fy-money-people',
        title: 'This fiscal year’s money and people',
        detail: 'Current-FY cost (internal labor, contractors, vendors, software, infra), cost avoidance, cost of inaction, net-new FTEs, and resourcing source (regional / GCC / third party). Use 0 rather than leaving a number blank.',
        unlocks: 'Headcount, Resourcing & Cost. Also feeds later Procurement screening (“is sourcing needed?”).',
      },
      {
        id: 'what-changes',
        title: 'What actually changes',
        detail: 'Process/capability, an existing bank application (EAI name), a new application name, and/or people uplift — more than one can apply. Call Discovery unless build is already scoped and funded.',
        unlocks: 'Initiative Change Type and the conditional application fields.',
      },
    ],
    better: [
      {
        id: 'one-pager',
        title: 'Business case / one-pager (Word or PPT)',
        detail: 'The narrative Working Group reads first. Attach the same file after Submit via the paperclip on My Request if it will not fit in the form.',
        unlocks: 'Richer problem/outcome paste blocks; recommended (not required) attachment.',
      },
      {
        id: 'cost-workbook',
        title: 'Cost and headcount workbook (Excel)',
        detail: 'Anything that backs FY spend, avoidance, inaction, FTEs, 3-year run-rate savings, and lifetime revenue band.',
        unlocks: 'Numeric fields plus Organizational Impact scoring inputs (savings / revenue dropdowns).',
      },
      {
        id: 'sponsor-email',
        title: 'Sponsor / owner confirmation (email or notes)',
        detail: 'Who will sponsor, own, and COO-execute — in their own words if possible.',
        unlocks: 'Person lookups and a defensible “why these names” rationale.',
      },
      {
        id: 'arch-notes',
        title: 'Architecture or inventory notes',
        detail: 'Existing vs new systems, data exchanged, personal information in scope. Enough to pick change types and search EAI — not a C4L1 diagram.',
        unlocks: 'Change type, existing/new application, assumptions/dependencies counts.',
      },
      {
        id: 'issue-extract',
        title: 'Issue / audit extract (if you are claiming one)',
        detail: 'Issue ID, type, rating, due date from Makoto (or the live issue system). “Don’t Know” is an allowed Stage 1 answer — do not invent an ID.',
        unlocks: 'Organizational Impact issue alignment and conditional issue-detail fields.',
      },
      {
        id: 'strategy-note',
        title: 'Strategy / OKR / portfolio note (if claiming strategic alignment)',
        detail: 'Which published strategic priority and goal this supports. If it does not, answer No — Working Group can correct this later, but guessing is worse.',
        unlocks: 'Supports strategic priority + conditional priority/goal fields.',
      },
    ],
    doNotWait: [
      {
        id: 'c4l1',
        title: 'C4L1 / Ardoq C4.1 conceptual diagram',
        detail: 'The assigned Solution Architect attests this during Stage 3 EA Screening. The requestor does not produce it in Ardoq.',
        unlocks: 'Do not block Stage 1 waiting for it.',
      },
      {
        id: 'gov-pptx',
        title: 'Governance Forum PowerPoint',
        detail: 'ServiceNow auto-builds this from Stage 1 fields when the idea reaches Final Review. There is no later chance to “clean up the story.”',
        unlocks: 'Accuracy in the packet is the deck the Forum reads.',
      },
      {
        id: 'native-view',
        title: 'Stages 2–5 Native View answers',
        detail: 'Feasibility Fit, EA Screening, Procurement screening, and Final Approval are other personas in Native View — expectation-setting only in this packet.',
        unlocks: 'After-submit briefing, not fillable Stage 1 fields.',
      },
    ],
  },
  sections: [
    {
      id: 'scope',
      title: 'Scope & Classification',
      intro: 'Top of the Global Initiatives Intake Request form. A demand is treated as global if it is multi-region, supports a global strategic program, or exceeds the material-spend threshold. These answers also feed later scoring.',
      fields: [
        {
          id: 'initiative_type',
          label: 'What is the initiative type of your ask?',
          location: 'Top of the form — Scope & Classification',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'partial',
          options: [
            {
              value: 'Change the Bank - Strategic Transformation, New Product / Capability',
              confidence: 'partial',
              definition: 'Confirmed selected example. Other Change-the-Bank / Run-the-Bank flavors exist but were not fully listed.',
            },
            {
              value: 'Change the Bank — new application / new product (closest match if the exact label differs)',
              confidence: 'unconfirmed',
            },
            {
              value: 'Run the Bank — BAU / keep-the-lights-on / incident-problem (only if this is maintenance, not a new capability)',
              confidence: 'unconfirmed',
            },
          ],
          optionsNote: 'Full dropdown was never expanded in training. Change-the-Bank = new or materially advanced capability, structural change, dedicated funding and project governance (including regulatory remediation treated as change). Run-the-Bank = BAU / KTLO / patching / incident-problem / license refresh with no material capability advance. RtB still completes this form; it is a checkpoint, not currently a GFD funding vote. Do not conflate this with Initiative Change Type later in the form.',
          clickPath: [
            'Find the first dropdown: “What is the initiative type of your ask?” (asterisk = required).',
            'Click the dropdown.',
            'Select the recommended value below. If the live wording differs, pick the closest Change-the-Bank / new capability option — not a Run-the-Bank / BAU option.',
          ],
          copyGuidance: 'This is a dropdown — do not paste. Click the option.',
          warn: 'This field is independent of “Initiative Change Type” later. Do not skip that later multi-select.',
          source: 'Doc 3, 3.02; Transcript 06:26–09:20',
        },
        {
          id: 'impacted_regions',
          label: 'Please indicate which region(s) are impacted by your initiative.',
          location: 'Scope & Classification',
          filledBy: 'Initiative Requestor',
          fieldType: 'multi_select',
          required: true,
          playbookConfidence: 'partial',
          options: [
            { value: 'Americas', confidence: 'confirmed' },
            { value: 'Head Office', confidence: 'confirmed' },
            { value: 'EMEA', confidence: 'partial' },
            { value: 'APAC', confidence: 'partial' },
          ],
          optionsNote: 'Americas and Head Office were seen selected. EMEA was spoken as “Amia” in training. APAC exists as a geographic division. Confirm whether Head Office is a fourth value or a special case.',
          clickPath: [
            'Find the multi-select tagged “Please indicate which region(s) are impacted by your initiative.”',
            'Click into the field. Select every region that will feel the effect, even indirectly.',
            'Selecting more than one region marks the initiative as global.',
          ],
          copyGuidance: 'Multi-select — click each recommended region. Before submitting, check three things Working Group will ask later: do the regions converge on policy/process, is regional buy-in in the timeline, and are there regional blockers GFD should know about.',
          source: 'Doc 3, 3.02; Doc 2 Global Initiative Trigger Criteria',
        },
        {
          id: 'total_cost_band',
          label: 'What is the estimated initiative cost for the entire lifecycle? (includes CapEx and OpEx, excludes post-implementation run costs and sunken costs)',
          location: 'Scope & Classification',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'partial',
          options: [
            { value: 'Not Known', confidence: 'partial' },
            { value: '<$500K', confidence: 'partial' },
            { value: '$500K–$1M', confidence: 'partial' },
            { value: '$1M–$3M', confidence: 'unconfirmed' },
            { value: '$3–5M', confidence: 'partial' },
            { value: '$5M–$10M', confidence: 'partial' },
            { value: '$10M–$20M', confidence: 'partial' },
            { value: '$20M–$50M', confidence: 'unconfirmed' },
            { value: '$50M–$100M', confidence: 'partial' },
            { value: '>$100M', confidence: 'partial' },
          ],
          optionsNote: 'Band list reconstructed from training + the GFD dashboard (which showed $10–20M and $50–100M). Official TTT help text is entire-lifecycle CapEx + OpEx excluding post-implementation run and sunk costs. The live demo called the same control “estimated external cost for the remaining lifecycle.” Match the label and bands on screen.',
          clickPath: [
            'Open the cost-band dropdown. Use the wording on screen if it differs from this packet.',
            'Select the recommended band. If a closer live band exists, pick the live band and tell the consultant.',
          ],
          copyGuidance: 'Dropdown. Include CapEx + project OpEx (technology, vendors, staffing, consulting, testing, training, change management). Exclude ongoing BAU after go-live and money already spent.',
          source: 'Doc 3, 3.02; Transcript 06:26–07:40',
        },
        {
          id: 'delivery_timeline',
          label: 'What is the estimate delivery timeline for this initiative?',
          location: 'Scope & Classification',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'partial',
          options: [
            { value: 'Not Known / Follow up needed', confidence: 'partial' },
            { value: '3 months or less (label unconfirmed)', confidence: 'unconfirmed' },
            { value: '3–6 months', confidence: 'partial' },
            { value: '6–12 Months', confidence: 'partial' },
            { value: '12–18 months', confidence: 'partial' },
            { value: 'Multi-year / multi-year dependencies', confidence: 'partial' },
          ],
          optionsNote: 'Dashboard grouped initiatives by these bands, including Not Known / Follow up. Confirm live capitalization (Months vs months).',
          clickPath: [
            'Open the delivery-timeline dropdown.',
            'Select the recommended band. Measure from expected approval through go-live, including design, build, test, deploy, training, and change management — not just development.',
          ],
          copyGuidance: 'Dropdown. Confirm live wording (Months vs months).',
          source: 'Doc 3, 3.02; Transcript 06:44–07:40',
        },
      ],
    },
    {
      id: 'strategic',
      title: 'Strategic Alignment',
      intro: 'One of GFD’s two mandatory key controls. Answering No to the gating question is a functional non-starter.',
      fields: [
        {
          id: 'supports_strategic_priority',
          label: 'Does your initiative support a primary Global Strategic Priority?',
          location: 'Strategic Alignment',
          filledBy: 'Initiative Requestor',
          fieldType: 'radio_yn',
          required: true,
          playbookConfidence: 'confirmed',
          options: yn,
          clickPath: [
            'Find the Yes/No control for “Does your initiative support a primary Global Strategic Priority?”',
            'Select Yes. This reveals the two fields below.',
          ],
          copyGuidance: 'Click Yes. Do not select No unless leadership has explicitly decided this has no strategic home — it will struggle in Feasibility Fit.',
          source: 'Doc 3, 3.03',
        },
        {
          id: 'strategic_priority',
          label: 'Which Global Strategic Priority is being supported by your initiative?',
          location: 'Strategic Alignment',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'confirmed',
          showWhen: { fieldId: 'supports_strategic_priority', equals: 'Yes' },
          options: [
            { value: 'Capability Uplift', confidence: 'confirmed' },
            { value: 'Direct Saving', confidence: 'confirmed', definition: 'Also spoken as “Direct Savings” — same value.' },
            { value: 'Revenue Enablement', confidence: 'confirmed' },
            { value: 'Risk Reduction', confidence: 'confirmed' },
          ],
          clickPath: [
            'Open “Which Global Strategic Priority is being supported by your initiative?”',
            'Select the recommended priority. This filters the Strategic Goal dropdown.',
          ],
          copyGuidance: 'Pick the primary driver, not every benefit. Hover the info tooltip if unsure.',
          source: 'Doc 3, 3.03; Doc 2 Global Strategic Priorities',
        },
        {
          id: 'strategic_goal',
          label: 'Which Strategic Goal (aligned to Strategic Priority selected above) does your initiative primarily support?',
          location: 'Strategic Alignment',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'partial',
          showWhen: { fieldId: 'supports_strategic_priority', equals: 'Yes' },
          options: [
            { value: 'Capability Uplift – Tech', confidence: 'partial' },
            { value: 'Capability Uplift – Cyber', confidence: 'partial' },
          ],
          optionsNote: 'Only two goals were ever seen, both under Capability Uplift. If another priority is selected, pick the closest live goal and report the full list back.',
          clickPath: [
            'Open the Strategic Goal dropdown (options are filtered to the priority above).',
            'Select the recommended goal. If it is missing, screenshot the live list and pick the closest match.',
          ],
          copyGuidance: 'Dependent dropdown. Confirm the exact hyphen/en-dash in the live label.',
          source: 'Doc 3, 3.03; Transcript 07:40–09:20',
        },
      ],
    },
    {
      id: 'portfolio',
      title: 'Portfolio Alignment',
      intro: 'Second mandatory key control. Requesting and Beneficiary portfolios are independent — they do not have to match.',
      fields: [
        {
          id: 'requesting_portfolio',
          label: 'Which portfolio is driving this requirement? (Requesting Portfolio)',
          location: 'Portfolio Alignment',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'partial',
          options: [
            { value: 'T4T - Infra & Resilience', confidence: 'partial' },
            { value: 'T4T - Cyber', confidence: 'partial' },
            { value: 'T4T - AI & Process Automation', confidence: 'partial' },
            { value: 'T4T - Data', confidence: 'partial' },
            { value: 'T4B - Sales & Trading', confidence: 'partial' },
            { value: 'T4B - Transaction Banking', confidence: 'partial' },
            { value: 'T4B - Lending', confidence: 'partial' },
            { value: 'T4B - Reg/Compliance', confidence: 'partial' },
            { value: 'T4B - Corp Functions', confidence: 'partial' },
            { value: 'Transformation/Op Model - Change/PMO/Location', confidence: 'partial' },
            { value: 'Transformation/Op Model - Strategic Flex', confidence: 'partial' },
          ],
          optionsNote: 'Structure confirmed; exact naming (hyphens, “T4T - Cyber” vs longer labels) should be matched to the live list.',
          clickPath: [
            'Open Requesting Portfolio — “Which portfolio is driving this requirement?”',
            'Select the portfolio whose budget/ownership is sponsoring the ask.',
          ],
          copyGuidance: 'Dropdown. This may differ from the beneficiary.',
          source: 'Doc 3, 3.04; Doc 1; Transcript 10:03–10:29',
        },
        {
          id: 'beneficiary_portfolio',
          label: 'Which portfolio is the primary beneficiary of this initiative?',
          location: 'Portfolio Alignment',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'partial',
          options: [
            { value: 'T4T - Infra & Resilience', confidence: 'partial' },
            { value: 'T4T - Cyber', confidence: 'partial' },
            { value: 'T4T - AI & Process Automation', confidence: 'partial' },
            { value: 'T4T - Data', confidence: 'partial' },
            { value: 'T4B - Sales & Trading', confidence: 'partial' },
            { value: 'T4B - Transaction Banking', confidence: 'partial' },
            { value: 'T4B - Lending', confidence: 'partial' },
            { value: 'T4B - Reg/Compliance', confidence: 'partial' },
            { value: 'T4B - Corp Functions', confidence: 'partial' },
            { value: 'Transformation/Op Model - Change/PMO/Location', confidence: 'partial' },
            { value: 'Transformation/Op Model - Strategic Flex', confidence: 'partial' },
          ],
          clickPath: [
            'Open Beneficiary / Benefiting Portfolio.',
            'Select who receives the greatest business value, risk reduction, or capability enhancement.',
          ],
          copyGuidance: 'Same list as Requesting Portfolio. Same or different is both valid.',
          source: 'Doc 3, 3.04; Transcript 10:29–10:44',
        },
        {
          id: 'part_of_program',
          label: 'Is this part of an existing Program?',
          location: 'Portfolio Alignment',
          filledBy: 'Initiative Requestor',
          fieldType: 'radio_yn',
          required: true,
          playbookConfidence: 'confirmed',
          options: yn,
          clickPath: [
            'Select Yes or No for “Is this part of an existing Program?”',
            'Yes reveals a program lookup. If unsure, No is acceptable — it is traceability, not a blocker.',
          ],
          copyGuidance: 'Click Yes/No only.',
          source: 'Doc 3, 3.04; Transcript 10:44–11:37',
        },
        {
          id: 'which_program',
          label: 'Which program is your initiative apart of?',
          location: 'Portfolio Alignment (conditional)',
          filledBy: 'Initiative Requestor',
          fieldType: 'reference_lookup',
          required: 'conditional',
          playbookConfidence: 'confirmed',
          showWhen: { fieldId: 'part_of_program', equals: 'Yes' },
          clickPath: [
            'Click the program lookup (magnifying glass / type-ahead).',
            'Search the recommended program name. If it does not exist, leave a comment after submit and do not invent a program.',
          ],
          copyGuidance: 'Search the live Programs table. Example seen in training: “Cloud Transformation.”',
          source: 'Doc 3, 3.04',
        },
        {
          id: 'part_of_project',
          label: 'Is this part of an existing Project?',
          location: 'Portfolio Alignment',
          filledBy: 'Initiative Requestor',
          fieldType: 'radio_yn',
          required: true,
          playbookConfidence: 'confirmed',
          options: yn,
          clickPath: [
            'Select Yes or No for “Is this part of an existing Project?”',
            'Yes reveals a project lookup.',
          ],
          copyGuidance: 'Click Yes/No only.',
          source: 'Doc 3, 3.04',
        },
        {
          id: 'which_project',
          label: 'Which project is your initiative apart of?',
          location: 'Portfolio Alignment (conditional)',
          filledBy: 'Initiative Requestor',
          fieldType: 'reference_lookup',
          required: 'conditional',
          playbookConfidence: 'confirmed',
          showWhen: { fieldId: 'part_of_project', equals: 'Yes' },
          clickPath: [
            'Click the project lookup.',
            'Search the recommended project name. Example seen in training: “API Cloud Infrastructure Build.”',
          ],
          copyGuidance: 'Live Projects table — not a fixed list.',
          source: 'Doc 3, 3.04',
        },
      ],
    },
    {
      id: 'business_case',
      title: 'Business Case & Ownership',
      intro: 'Free-text quality here is what Working Group reviews first. Names must be real people in the ServiceNow user table.',
      fields: [
        {
          id: 'initiative_name',
          label: 'Initiative Name',
          location: 'Business Case & Initiative Details',
          filledBy: 'Initiative Requestor',
          fieldType: 'free_text_short',
          required: true,
          playbookConfidence: 'confirmed',
          clickPath: [
            'Click the Initiative Name box.',
            'Select any placeholder text and paste the recommended name. Keep it short and distinguishable.',
          ],
          copyGuidance: 'This title appears in list views, dashboards, and the auto-generated governance PowerPoint.',
          source: 'Doc 3, 3.05; Transcript 11:37',
        },
        {
          id: 'intended_outcome',
          label: 'What is the intended outcome of the initiative?',
          location: 'Business Case & Initiative Details',
          filledBy: 'Initiative Requestor',
          fieldType: 'free_text_long',
          required: true,
          playbookConfidence: 'confirmed',
          characterLimit: 4000,
          clickPath: [
            'Click the intended-outcome textarea (Train-the-Trainer lists this immediately after Initiative Name).',
            'Paste the full block. Include measurable KPIs, not a restated goal.',
          ],
          copyGuidance: 'Outcomes and how you will measure them. This is the future-state Working Group reads before the problem statement.',
          source: 'Doc 3, 3.05; Transcript 13:00–13:44',
        },
        {
          id: 'problem_statement',
          label: 'Please provide a short description of the problem statement',
          location: 'Business Case & Initiative Details',
          filledBy: 'Initiative Requestor',
          fieldType: 'free_text_long',
          required: true,
          playbookConfidence: 'confirmed',
          characterLimit: 4000,
          clickPath: [
            'Click the problem-statement textarea.',
            'Paste the full block below. Do not shorten it to a title.',
          ],
          copyGuidance: 'Describe the business/operational/technology/regulatory problem, current-state gaps, and why it matters. ~4,000 character limit (stated verbally, not verified).',
          warn: 'Three-word answers get sent back to Draft.',
          source: 'Doc 3, 3.05; Transcript 11:37–13:00, 51:19–53:31',
        },
        {
          id: 'initiative_owner',
          label: 'Initiative Owner',
          location: 'Business Case & Initiative Details',
          filledBy: 'Initiative Requestor',
          fieldType: 'reference_lookup',
          required: true,
          playbookConfidence: 'confirmed',
          clickPath: [
            'Click Initiative Owner (person lookup).',
            'Search the recommended name. Select the matching SMBC user record — do not type a name that is not in the directory.',
          ],
          copyGuidance: 'Who will drive this to completion and can answer questions about the problem and outcome.',
          source: 'Doc 3, 3.05; Transcript 13:00–13:44',
        },
        {
          id: 'executive_sponsor',
          label: 'CxO or Senior Business Executive Sponsor',
          location: 'Business Case & Initiative Details',
          filledBy: 'Initiative Requestor',
          fieldType: 'reference_lookup',
          required: true,
          playbookConfidence: 'confirmed',
          clickPath: [
            'Click CxO or Senior Business Executive Sponsor.',
            'Search and select the recommended executive.',
          ],
          copyGuidance: 'Executive-level sponsor. Must resolve in the user table.',
          source: 'Doc 3, 3.05; Transcript 13:44',
        },
        {
          id: 'coo_execution_lead',
          label: 'COO Execution Lead',
          location: 'Business Case & Initiative Details',
          filledBy: 'Initiative Requestor',
          fieldType: 'reference_lookup',
          required: true,
          playbookConfidence: 'confirmed',
          clickPath: [
            'Click COO Execution Lead.',
            'Search and select the recommended operations/delivery lead.',
          ],
          copyGuidance: 'Person supporting delivery from the COO / operations side.',
          source: 'Doc 3, 3.05; Transcript 13:44',
        },
      ],
    },
    {
      id: 'change_type',
      title: 'Initiative Change Type, Phase, Assumptions & Dependencies',
      intro: 'Four change-type categories; more than one can apply. Assumptions and dependencies are threshold counts, not vibes.',
      fields: [
        {
          id: 'change_type',
          label: 'What is the Initiative Change Type? (Select all that apply)',
          location: 'Immediately after Business Case & Ownership',
          filledBy: 'Initiative Requestor',
          fieldType: 'multi_select',
          required: true,
          playbookConfidence: 'confirmed',
          options: [
            { value: 'Capability/Process Definition and/or Enhancement', confidence: 'confirmed' },
            { value: 'Change to an existing bank application', confidence: 'confirmed' },
            { value: 'Onboarding a new (bank) application', confidence: 'confirmed' },
            { value: 'People Uplift', confidence: 'confirmed' },
          ],
          clickPath: [
            'Find “What is the Initiative Change Type? (Select all that apply)” — four categories.',
            'Check every recommended box. Checking “Change to an existing bank application” or “Onboarding a new application” reveals follow-up fields.',
          ],
          copyGuidance: 'Ask plainly: process, existing system, brand-new system, or how people work — multiple can apply.',
          source: 'Doc 3, 3.06; Transcript 13:44–15:19',
        },
        {
          id: 'existing_application',
          label: 'Which existing business application is impacted?',
          location: 'Same section (conditional)',
          filledBy: 'Initiative Requestor',
          fieldType: 'reference_lookup',
          required: 'conditional',
          playbookConfidence: 'confirmed',
          showWhen: { fieldId: 'change_type', includes: 'Change to an existing bank application' },
          clickPath: [
            'When the application lookup appears, click it.',
            'Search the Enterprise Architecture Inventory for the recommended application name and select it.',
          ],
          copyGuidance: 'Governed EAI table — search rather than invent wording. Training example: “ServiceNow”.',
          source: 'Doc 3, 3.06; Transcript 13:44–15:19',
        },
        {
          id: 'new_application_name',
          label: 'New Bank Application (name)',
          location: 'Same section (conditional)',
          filledBy: 'Initiative Requestor',
          fieldType: 'free_text_short',
          required: 'conditional',
          playbookConfidence: 'unconfirmed',
          showWhen: { fieldId: 'change_type', includes: 'Onboarding a new (bank) application' },
          clickPath: [
            'When “New Bank Application” appears, click the field.',
            'Paste the recommended application name.',
          ],
          copyGuidance: 'Field mechanism inferred from transcript (free text or reference — unconfirmed).',
          source: 'Transcript 15:19, 44:33–45:19',
        },
        {
          id: 'discovery_or_execution',
          label: 'Is this initiative Discovery or Execution?',
          location: 'Same section',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'confirmed',
          options: [
            { value: 'Discovery', confidence: 'confirmed', definition: 'Still gathering requirements, evaluating options, scoping.' },
            { value: 'Execution', confidence: 'confirmed', definition: 'Actively building/deploying an already-scoped solution.' },
          ],
          clickPath: [
            'Open Discovery or Execution.',
            'Select the recommended value.',
          ],
          copyGuidance: 'Discovery unless build has already been scoped and funded to execute.',
          source: 'Doc 3, 3.06; Transcript 15:19–15:49',
        },
        {
          id: 'assumptions',
          label: 'Does your initiative have assumptions?',
          location: 'Same section',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'confirmed',
          options: [
            { value: 'Many Assumptions – high uncertainty', definition: '20+ key assumptions', confidence: 'confirmed' },
            { value: 'Some Assumptions – mostly based on reasonable expectations and require validation', definition: '10–19 assumptions. Live screenshot used “need validation.”', confidence: 'confirmed' },
            { value: 'Minimal Assumptions – supported by data or evidence', definition: '1–9 assumptions', confidence: 'confirmed' },
            { value: 'No Assumptions – idea is fully validated and fact-based', definition: '0 assumptions', confidence: 'confirmed' },
          ],
          clickPath: [
            'Open the assumptions dropdown.',
            'Count key assumptions across business case, feasibility, resources, and regulatory impact, then pick the matching tier — not a feeling.',
          ],
          copyGuidance: 'Threshold-based: 0 / 1–9 / 10–19 / 20+.',
          source: 'Doc 3, 3.06; Doc 2 Assumptions & Dependencies Threshold Criteria',
        },
        {
          id: 'dependencies',
          label: 'Does your initiative have dependencies?',
          location: 'Same section',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'confirmed',
          options: [
            { value: 'Many Critical Dependencies – high risk to success', definition: '20+ key dependencies', confidence: 'confirmed' },
            { value: 'Some Important Dependencies – could hinder progress', definition: '10–19 dependencies', confidence: 'confirmed' },
            { value: 'Minimal Dependencies – manageable impact', definition: '1–9 dependencies', confidence: 'confirmed' },
            { value: 'No Dependencies – fully self-contained and controllable', definition: '0 dependencies', confidence: 'confirmed' },
          ],
          clickPath: [
            'Open the dependencies dropdown.',
            'Count external teams/systems/deliverables this cannot proceed without, then pick the matching tier.',
          ],
          copyGuidance: 'Same 0 / 1–9 / 10–19 / 20+ scale.',
          source: 'Doc 3, 3.06; Doc 2',
        },
      ],
    },
    {
      id: 'resourcing',
      title: 'Headcount, Resourcing & Cost',
      intro: 'Current-fiscal-year numbers. Enter 0 rather than leaving numeric fields blank.',
      fields: [
        {
          id: 'additional_headcount',
          label: 'Will this initiative require any additional headcount (Net New)? If so, please provide the total number of FTEs required.',
          location: 'Headcount / Resourcing / Cost',
          filledBy: 'Initiative Requestor',
          fieldType: 'number',
          required: true,
          playbookConfidence: 'confirmed',
          clickPath: [
            'Click the net-new FTE number field.',
            'Type the recommended number (0 if none). This is current fiscal year permanent/sustaining FTEs.',
          ],
          copyGuidance: 'Number only. Future-year headcount was described as a later enhancement — do not try to encode it here.',
          source: 'Doc 3, 3.07; Transcript 16:00–17:00',
        },
        {
          id: 'resourcing_source',
          label: 'Will you be resourcing from any of the below? (Check all that apply)',
          location: 'Headcount / Resourcing / Cost',
          filledBy: 'Initiative Requestor',
          fieldType: 'multi_select',
          required: true,
          playbookConfidence: 'partial',
          options: [
            { value: 'Internal – Regional', confidence: 'partial' },
            { value: 'Internal – Global Capability Center', confidence: 'partial' },
            { value: 'External / Third-Party Resources', confidence: 'partial' },
          ],
          clickPath: [
            'Check every recommended resourcing source. Multiple selections are expected on larger initiatives.',
          ],
          copyGuidance: 'Confirm live labels. “External / Third-Party Resources” was seen selected verbatim.',
          source: 'Doc 3, 3.07; Transcript 17:00–17:40',
        },
        {
          id: 'fy_cost',
          label: 'What is the estimated cost for the current fiscal year?',
          location: 'Headcount / Resourcing / Cost',
          filledBy: 'Initiative Requestor',
          fieldType: 'currency',
          required: true,
          playbookConfidence: 'confirmed',
          clickPath: [
            'Click Current Fiscal Year Cost.',
            'Enter the recommended amount (no narrative). Include internal labor, contractors, vendors, software, infrastructure, and other delivery costs for this FY.',
          ],
          copyGuidance: 'Currency/number. Also appears later on the back-end Business Case tab as “Current Fiscal Year Cost.”',
          source: 'Doc 3, 3.07; 2.05; Transcript 17:40',
        },
        {
          id: 'cost_avoidance',
          label: 'Will there be any cost avoidance? Please provide the number below.',
          location: 'Headcount / Resourcing / Cost',
          filledBy: 'Initiative Requestor',
          fieldType: 'currency',
          required: true,
          playbookConfidence: 'confirmed',
          clickPath: [
            'Click cost avoidance.',
            'Enter the recommended amount, or 0 if none.',
          ],
          copyGuidance: 'Future costs this initiative prevents (penalties, infra expansion, manual-effort growth, tech debt).',
          source: 'Doc 3, 3.07; 2.05; Transcript 17:40',
        },
        {
          id: 'cost_of_inaction',
          label: 'Is there a cost of inaction or delayed actioning of this initiative? If yes, please quantify below.',
          location: 'Headcount / Resourcing / Cost',
          filledBy: 'Initiative Requestor',
          fieldType: 'currency',
          required: true,
          playbookConfidence: 'confirmed',
          clickPath: [
            'Click cost of inaction.',
            'Enter the recommended amount, or 0 if none.',
          ],
          copyGuidance: 'What it costs financially, operationally, in regulatory exposure, reputation, or client relationships if this is delayed or unfunded.',
          source: 'Doc 3, 3.07; 2.05; Transcript 17:40',
        },
      ],
    },
    {
      id: 'org_impact',
      title: 'Organizational Impact (Scoring Inputs)',
      intro: 'These four answers feed the Strategic Alignment Score after Feasibility Fit. Accuracy here changes prioritization.',
      fields: [
        {
          id: 'issue_alignment',
          label: 'Does this initiative tie to an existing SMBC issue entry? If so, at what level?',
          location: 'Organizational Impact',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'partial',
          options: [
            { value: 'High-Rated (Regulatory Findings)', confidence: 'partial' },
            { value: 'High Rated Issues (Non-Regulatory Findings)', confidence: 'partial' },
            { value: 'Medium-Rated Issues', confidence: 'partial' },
            { value: 'Low-Rated Issues', confidence: 'partial' },
            { value: 'No Issues Identified', confidence: 'partial' },
            { value: "Don't Know / Not Sure", confidence: 'partial' },
          ],
          clickPath: [
            'Open the issue-alignment dropdown.',
            'Select the recommended value. Anything other than “No Issues Identified” / “Don’t Know” may reveal Issue ID, Type, Rating, and Due Date — paste those if they appear.',
          ],
          copyGuidance: 'Likely maps to SMBC issue management (referred to as Makoto or similar in training). Don’t Know is acceptable if the requestor does not have the issue ID.',
          source: 'Doc 3, 3.08; Transcript 17:40–19:22',
        },
        {
          id: 'issue_details',
          label: 'Issue ID, Type of Issue, Rating of the Issue, and Issue Due Date',
          location: 'Organizational Impact (conditional)',
          filledBy: 'Initiative Requestor',
          fieldType: 'free_text_long',
          required: 'conditional',
          playbookConfidence: 'partial',
          showWhen: {
            fieldId: 'issue_alignment',
            notEqualsAny: ['No Issues Identified', "Don't Know / Not Sure", ''],
          },
          clickPath: [
            'If follow-up issue fields appear, paste the recommended ID / type / rating / due date into the matching boxes (mostly free text, character-limited help text in training).',
          ],
          copyGuidance: 'Skip if the live form did not reveal extra fields.',
          source: 'Transcript 17:40–19:22',
        },
        {
          id: 'capability_uplift_level',
          label: 'Does this initiative uplift CIO/CDAO/CISO/Operations capabilities?',
          location: 'Organizational Impact',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'partial',
          options: [
            { value: 'Impacts an L1 capability', confidence: 'partial' },
            { value: 'Moderately impacts a single L2 capability', confidence: 'partial' },
            { value: 'Minor impacts to L2', confidence: 'partial' },
            { value: 'No capability uplift / Not known', confidence: 'partial' },
          ],
          clickPath: [
            'Open the capability-uplift dropdown.',
            'Select the recommended level. Anything other than “No capability uplift / Not known” reveals a capability lookup.',
          ],
          copyGuidance: 'Confirm live wording.',
          source: 'Doc 3, 3.08; Transcript 19:22–20:10',
        },
        {
          id: 'impacted_capabilities',
          label: 'If so, what capabilities?',
          location: 'Organizational Impact (conditional)',
          filledBy: 'Initiative Requestor',
          fieldType: 'reference_lookup',
          required: 'conditional',
          playbookConfidence: 'partial',
          showWhen: { fieldId: 'capability_uplift_level', notEquals: 'No capability uplift / Not known' },
          optionsNote: 'Live “Business Capabilities” table. Search these L0 → L1 labels rather than inventing names. Technology: Technology Strategy & Governance; Technology Delivery & Engineering; Core Infrastructure & Workplace; Corporate Functions Technology. Operations: Market Ops; Commercial Banking; Loan Servicing & Syndication; Middle Office; Treasury; Settlement Ops; Business Management; Anti-Fraud. Data: Data Strategy / Data Management Business Case; Data Management Program & Funding; Architecture – Business, Data & Technology; Business Data Knowledge; Data Quality Management; Governance – Data; Data Management Operations, Risk & Control; Analytics Management. Cyber (NIST-shaped): Govern; Identify; Protect; Detect; Respond; Recover; Extend. Training also showed an umbrella “Cyber Security Management” — pick the live label if that is what the lookup returns.',
          clickPath: [
            'Click the capabilities lookup / multi-select.',
            'Search the recommended capability names. Prefer inventory labels over invented wording. Drill to L2 if the search requires it.',
          ],
          copyGuidance: 'Search the governed table. Flag the live label back to the consultant if it differs.',
          source: 'Doc 3, 3.08; Doc 2 Capability Model; Transcript 19:22–20:10',
        },
        {
          id: 'runrate_savings',
          label: 'Does this initiative deliver run-rate cost savings? If so, how much is expected over a 3-year period?',
          location: 'Organizational Impact',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'partial',
          options: [
            { value: 'No cost savings', confidence: 'partial' },
            { value: '<$1M', confidence: 'partial' },
            { value: '$1M–$5M', confidence: 'partial' },
            { value: '$5M–$10M', confidence: 'unconfirmed' },
            { value: '>$10M', confidence: 'partial' },
          ],
          optionsNote: 'Spoken list ran greater than $10M down to less than $1M plus no savings. $5–10M is inferred and unconfirmed.',
          clickPath: [
            'Open the 3-year run-rate savings dropdown.',
            'Select the recommended band. Confirm live cut points.',
          ],
          copyGuidance: '3-year view, not single-year.',
          source: 'Doc 3, 3.08; Transcript 20:10–20:36',
        },
        {
          id: 'revenue_growth',
          label: 'Does this initiative enable cumulative incremental revenue growth over the lifetime of the initiative? If so, how much?',
          location: 'Organizational Impact',
          filledBy: 'Initiative Requestor',
          fieldType: 'single_select',
          required: true,
          playbookConfidence: 'partial',
          options: [
            { value: 'No revenue connection / Not known', confidence: 'partial' },
            { value: '<$10M (supports a function that eventually touches revenue)', confidence: 'partial' },
            { value: '$10M–$50M', confidence: 'unconfirmed' },
            { value: '$50M–$100M', confidence: 'unconfirmed' },
            { value: '>$100M', confidence: 'partial' },
          ],
          optionsNote: 'Spoken list emphasized >$100M, <$10M, no connection, and not-known. Middle bands are inferred from “values are a little bit bigger” than the savings scale.',
          clickPath: [
            'Open the revenue-growth dropdown.',
            'Select the recommended band. Enabling infrastructure that only indirectly supports revenue still uses the “supports a function…” style option if it exists.',
          ],
          copyGuidance: 'Lifetime incremental revenue, not annual.',
          source: 'Doc 3, 3.08; Transcript 20:36–21:49',
        },
      ],
    },
    {
      id: 'submit',
      title: 'Attachments & Submit',
      intro: 'Submit once required fields are complete. You will get an IDEA####### number and an email.',
      fields: [
        {
          id: 'additional_comments',
          label: 'Any additional comments or notes? (optional — Activity tab)',
          location: 'Submitting the Request',
          filledBy: 'Initiative Requestor',
          fieldType: 'free_text_long',
          required: false,
          playbookConfidence: 'confirmed',
          clickPath: [
            'Optional. Add a comment on the Activity tab if something did not fit a field.',
          ],
          copyGuidance: 'Optional. Usable now and at any later stage to reach a fulfiller.',
          source: 'Doc 3, 3.09',
        },
        {
          id: 'submit_action',
          label: 'Submit',
          location: 'Bottom / footer of the catalog form',
          filledBy: 'Initiative Requestor',
          fieldType: 'action_button',
          required: true,
          playbookConfidence: 'confirmed',
          clickPath: [
            'Scroll the form and confirm every asterisk field is filled.',
            'Do not click Save as Draft.',
            'Click Submit.',
            'Wait for the My Request / ticket page. Copy the IDEA number from the confirmation and send it back to the consultant.',
            'Optional: use the paperclip on My Request to attach the business case and cost support.',
            'Optional: add an Additional Comment if something did not fit a field (e.g. planned integrations).',
          ],
          copyGuidance: 'After submit you should receive a confirmation email with IDEA#######. GFD Working Group is notified separately. A “Ensure Feasibility Fit” task is created for them — you do not complete that task.',
          warn: 'Save as Draft does not save. If the page errors, do not close the tab — screenshot and contact the consultant.',
          source: 'Doc 3, 3.09; Transcript 21:49–25:26',
        },
      ],
    },
  ],
  afterSubmit: [
    {
      id: 'stage2',
      stage: 'Stage 2 — Feasibility Fit',
      persona: 'GFD Working Group',
      interfaceName: 'ServiceNow Native View',
      summary: 'Working Group reviews completeness and the two key controls (Strategic Alignment and Portfolio Alignment). They can edit fields directly or Send to Draft. After Fit closes, ServiceNow calculates a Strategic Alignment Score (issue alignment, run-rate savings, capability uplift, revenue growth) and a Cost & Delivery Execution Score (cost band + timeline). Recalculate Score if they change those fields.',
      requestorShould: [
        'Stay reachable on email / Additional Comments (requestor-visible). Work Notes are internal — you will not see those.',
        'If sent to Draft: start in Employee Center → My Requests. If that is missing, Native View → All → Idea → My Ideas. Read Activity comments, edit the flagged fields, right-click the gray header and Save (stays on the form) or Update (returns to the list), then click Re-Submit (this creates a new Feasibility Fit task).',
        'Do not be surprised if they correct Strategic Priority or Portfolio rather than bouncing you — that is allowed. A courtesy comment is best practice, not guaranteed.',
      ],
    },
    {
      id: 'stage3',
      stage: 'Stage 3 — EA Screening',
      persona: 'Solution Architect (with AI / Architecture / Cyber / Data leads)',
      interfaceName: 'ServiceNow Native View',
      summary: 'Runs in parallel with Procurement screening after Feasibility Fit closes. Architect answers Screening-tab questions (AI, Architecture, Cyber, Data) and must attest an Ardoq C4.1 / C4L1 conceptual diagram is attached. You do not fill those dropdowns.',
      requestorShould: [
        'Expect to be consulted so the C4L1 / Ardoq C4.1 diagram is accurate — you do not have to draft it in Ardoq.',
        'Architect recommendations go in Work Notes (internal). You will not see those.',
      ],
    },
    {
      id: 'stage4',
      stage: 'Stage 4 — Procurement / Ops Screening',
      persona: 'Procurement / Ops Screening Group',
      interfaceName: 'ServiceNow Native View',
      summary: 'Lightest review: they check Resourcing Level on the Business Case tab and answer one Screening-tab question — Does this request require Sourcing/Procurement screening? Live values seen: Yes, new purchase, new vendor; Yes, change to existing vendor or contract; No, internal build or use only; Hybrid internal build with third party and services.',
      requestorShould: [
        'No action unless they comment asking for vendor context. Accurate resourcing (GCC / regional / third party) is what they read.',
        'Both Stage 3 and Stage 4 must be Closed Complete before Final Approval.',
      ],
    },
    {
      id: 'stage5',
      stage: 'Stage 5 — Final Approval',
      persona: 'GFD Governance Forum (Working Group in ServiceNow)',
      interfaceName: 'ServiceNow Native View',
      summary: 'Idea moves to Final Review. ServiceNow auto-builds a PowerPoint from your Stage 1 fields (it can take a minute or two to attach). Forum meets offline. One Working Group member records GFD Governance Outcome Status: Pre-Approved; Pre-Approved to Execute; Deferred - High Urgency; Deferred - Standard; Deferred - Denied. Approved / Pre-Approved converts the Idea to a Demand in SPM. Conditional requirements on the Idea flow into that Demand.',
      requestorShould: [
        'Treat Stage 1 as the board deck — that is why the paste blocks are long.',
        'If approved or pre-approved: wait for the confirmation email; Demand Management / PMO pickup is partly still informal.',
        'If deferred: read comments. Deferred - Denied is a stop. Deferred - High Urgency / Deferred - Standard can be sent back to Draft for a full resubmit.',
        'PM assignment after approval and KISI/Kasai downstream gating were still open items at training time — do not hard-promise those steps.',
      ],
    },
  ],
};
